import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Spinner } from 'flowbite-react';
import toast from 'react-hot-toast';
import { FiSearch, FiX, FiPhone, FiCreditCard, FiHeart } from 'react-icons/fi';
import { MdEdit } from 'react-icons/md';
import { listPublicChits } from '../../api/guest';
import { getApiErrorMessage } from '../../utils/landingForm';
import { handoffToApp } from '../../utils/landingAuth';
import { useGuestSession } from '../../utils/useGuestSession';
import { getChitCardImage } from '../../utils/chitImage';
import { getPendingGuestAction } from '../../utils/pendingGuestAction';
import GuestOtpModal from '../../Components/GuestOtp/GuestOtpModal';
import GuestNextStepModal from '../../Components/GuestOtp/GuestNextStepModal';

function formatInr(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  });
}

const STEPS = [
  {
    icon: FiHeart,
    title: 'Show interest',
    body: 'Tell the company you are interested — no payment needed.',
  },
  {
    icon: FiPhone,
    title: 'Get a callback',
    body: 'They can reach you on your verified mobile number.',
  },
  {
    icon: FiCreditCard,
    title: 'Pay & join',
    body: 'When you are ready, join online and pay the instalment.',
  },
];

const GuestJoinPage = ({ theme }) => {
  const dark = theme === 'dark';
  const { loggedIn: guestIn, mobile: guestMobile, refresh: refreshGuestSession } =
    useGuestSession();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [otpOpen, setOtpOpen] = useState(false);
  const [nextStepOpen, setNextStepOpen] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(t);
  }, [search]);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const res = await listPublicChits({
        search: query || undefined,
        perPage: 24,
        currentPage: 0,
      });
      if (res?.status >= 400) {
        toast.error(getApiErrorMessage(res, 'Could not load chits'));
        setRows([]);
        return;
      }
      const result = res?.data?.result;
      const list = Array.isArray(result)
        ? result
        : result?.rows || result?.data || [];
      setRows(list);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not load chits'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(debouncedSearch);
  }, [debouncedSearch, load]);

  const handleEditMobile = () => {
    // Keep current guest session until a new mobile+OTP succeeds.
    setOtpOpen(true);
  };

  const clearSearch = () => {
    setSearch('');
    searchInputRef.current?.focus();
  };

  const hasQuery = Boolean(debouncedSearch);
  const panel = dark
    ? 'bg-[#FEFBFA] text-[#333333]'
    : 'bg-white text-[#333333] border border-[#3333334f]';

  return (
    <div
      className={`min-h-screen font-poppins ${
        dark
          ? 'bg-[#170603] bg-bannerBg bg-center bg-cover'
          : 'bg-[#FDE6E4]'
      }`}
    >
      {/* Clear gap under fixed navbar so content never looks glued to it */}
      <div className="pt-[128px] md:pt-[140px] pb-12 px-[5%] xl:px-[8%]">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div className="min-w-0">
            <h1
              className={`text-3xl md:text-4xl font-bold leading-tight ${
                dark ? 'text-white' : 'text-black'
              }`}
            >
              Explore{' '}
              <span
                className={
                  dark
                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-[#E26353] via-[#FBD4CE] to-[#903225]'
                    : 'text-primaryColor'
                }
              >
                chits
              </span>
            </h1>
            <p
              className={`mt-2 text-sm md:text-base max-w-xl ${
                dark ? 'text-[#E3E5ED]' : 'text-[#444444]'
              }`}
            >
              Show interest for a callback, or pay &amp; join when you are ready.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {guestIn ? (
              <div
                className={`inline-flex items-center gap-2 text-sm px-3 py-2 rounded-full font-medium shadow-sm ${
                  dark
                    ? 'bg-white/10 text-[#FBD4CE] border border-[#e3e5ed40]'
                    : 'bg-white text-primaryColor border border-[#FBD4CE]'
                }`}
              >
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                    dark
                      ? 'bg-emerald-500/20 text-emerald-300'
                      : 'bg-emerald-50 text-emerald-700'
                  }`}
                >
                  Verified
                </span>
                <span className="whitespace-nowrap">
                  +91 {guestMobile || '••••'}
                </span>
                <button
                  type="button"
                  onClick={handleEditMobile}
                  className={`inline-flex items-center gap-0.5 text-xs font-semibold hover:underline ${
                    dark ? 'text-[#FBD4CE]' : 'text-[#13224D]'
                  }`}
                  aria-label="Change mobile number"
                >
                  <MdEdit className="text-sm" />
                  Edit
                </button>
              </div>
            ) : (
              <Button
                color="failure"
                size="sm"
                className="bg-primaryColor hover:bg-[#c74132e6]"
                onClick={() => setOtpOpen(true)}
              >
                Continue with mobile
              </Button>
            )}
          </div>
        </div>

        <div
          className={`flex items-center gap-2 rounded-xl border px-3 mb-2 shadow-sm ${
            dark
              ? 'bg-[#FEFBFA] border-[#e3e5ed80]'
              : 'bg-white border-[#D0D5DD]'
          } focus-within:ring-2 focus-within:ring-primaryColor/25 focus-within:border-primaryColor`}
        >
          <FiSearch className="shrink-0 text-[#888888]" size={18} aria-hidden />
          <input
            id="join-chit-search"
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by chit name or reference id…"
            autoComplete="off"
            className="flex-1 min-w-0 border-0 bg-transparent py-3.5 text-sm text-[#333333] placeholder:text-[#888] focus:outline-none focus:ring-0"
          />
          {search ? (
            <button
              type="button"
              onClick={clearSearch}
              className="shrink-0 p-1.5 rounded-md text-[#666666] hover:bg-black/5"
              aria-label="Clear search"
            >
              <FiX size={18} />
            </button>
          ) : null}
        </div>

        <div
          className={`mb-6 flex flex-wrap items-center justify-between gap-2 text-xs ${
            dark ? 'text-[#E3E5ED]/90' : 'text-[#666666]'
          }`}
        >
          <span>
            {loading
              ? 'Searching…'
              : hasQuery
                ? `${rows.length} result${rows.length === 1 ? '' : 's'} for “${debouncedSearch}”`
                : `${rows.length} open chit${rows.length === 1 ? '' : 's'}`}
          </span>
          {!guestIn ? (
            <span>Verify mobile before interest or pay &amp; join.</span>
          ) : null}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          {STEPS.map(({ icon: Icon, title, body }, i) => (
            <div
              key={title}
              className={`rounded-xl px-4 py-3 flex gap-3 items-start shadow-sm ${panel}`}
            >
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primaryColor/10 text-primaryColor text-sm font-bold">
                {i + 1}
              </span>
              <div className="min-w-0">
                <p className="font-semibold text-sm flex items-center gap-1.5">
                  <Icon className="text-primaryColor shrink-0" size={14} />
                  {title}
                </p>
                <p className="text-xs text-[#666666] mt-0.5 leading-snug">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className={`flex justify-center py-16 rounded-2xl ${panel}`}>
            <Spinner size="lg" color="failure" />
          </div>
        ) : rows.length === 0 ? (
          <div
            className={`rounded-2xl px-6 py-12 md:py-14 text-center ${panel}`}
          >
            <p className="text-lg font-semibold mb-1">
              {hasQuery ? 'No matching chits' : 'No open chits right now'}
            </p>
            <p className="text-sm text-[#666666] max-w-md mx-auto mb-5">
              {hasQuery
                ? 'Try another name, or clear the search to see every open plan.'
                : 'Open plans from your chit company will appear here. You can also open a direct invite link if they shared one.'}
            </p>
            {hasQuery ? (
              <div className="flex flex-wrap justify-center gap-2">
                <Button color="light" size="sm" onClick={clearSearch}>
                  Clear search
                </Button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 md:gap-4">
            {rows.map((chit) => {
              const id = chit._id || chit.id;
              const filled = Array.isArray(chit.membersList)
                ? chit.membersList.length
                : chit.presentMemberCount ?? '—';
              return (
                <Link
                  key={id}
                  to={`/join/${id}`}
                  className={`group rounded-xl p-4 shadow-sm transition-all duration-200 ease-in-out hover:bg-[#13224D] hover:text-white hover:shadow-md ${panel}`}
                >
                  <div className="flex gap-3 items-start mb-3">
                    <img
                      src={getChitCardImage(chit)}
                      alt=""
                      className="w-11 h-11 rounded-full object-cover bg-[#FBD4CE]"
                    />
                    <div className="min-w-0 flex-1">
                      <h2 className="font-semibold truncate text-base md:text-lg">
                        {chit.chitName || 'Chit'}
                      </h2>
                      <p className="text-xs text-[#888888] group-hover:text-[#E3E5ED] truncate">
                        {chit.chit_id || id}
                      </p>
                    </div>
                    {chit.status ? (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-primaryColor/10 text-primaryColor capitalize group-hover:bg-white/15 group-hover:text-[#FBD4CE]">
                        {chit.status}
                      </span>
                    ) : null}
                  </div>
                  <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-[#888888] group-hover:text-[#E3E5ED]">
                        Value
                      </dt>
                      <dd className="font-medium">
                        {formatInr(chit.oneInstalmentTotalAmount)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-[#888888] group-hover:text-[#E3E5ED]">
                        Due
                      </dt>
                      <dd className="font-medium">
                        {formatInr(chit.oneInstalment)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-[#888888] group-hover:text-[#E3E5ED]">
                        Members
                      </dt>
                      <dd className="font-medium">
                        {filled}/{chit.membersCount || '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-[11px] uppercase tracking-wide text-[#888888] group-hover:text-[#E3E5ED]">
                        Type
                      </dt>
                      <dd className="font-medium capitalize">
                        {chit.durationType || chit.chitType || '—'}
                      </dd>
                    </div>
                  </dl>
                  <p className="mt-3 text-sm font-semibold text-primaryColor group-hover:text-[#FBD4CE]">
                    View details →
                  </p>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <GuestOtpModal
        isOpen={otpOpen}
        changeMobile={guestIn}
        onClose={() => setOtpOpen(false)}
        onGuestReady={() => {
          refreshGuestSession();
          setOtpOpen(false);
          // Continue with mobile / Edit (no Interest or Pay & join pending) → ask next step.
          const pending = getPendingGuestAction();
          if (!pending) {
            setNextStepOpen(true);
          }
        }}
      />

      <GuestNextStepModal
        isOpen={nextStepOpen}
        onClose={() => setNextStepOpen(false)}
        onExplore={() => setNextStepOpen(false)}
        onGoDashboard={() => {
          setNextStepOpen(false);
          handoffToApp({ target: '/dashboard' });
        }}
      />
    </div>
  );
};

export default GuestJoinPage;
